import dayjs from 'dayjs';
import { DateRangeBox } from 'devextreme-react';
import Button from 'devextreme-react/button';
import { RequiredRule, Validator } from 'devextreme-react/validator';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import { useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetCompany } from '../../api/company';
import { useAuth } from '../../contexts/auth';
import { CompanyTypes } from '../../types/enums';
import { formatIsoDate } from '../../utils/date-utils';
import './SearchPanel.scss';

export default function SearchPanel() {
	const navigate = useNavigate();
	const { user } = useAuth();
	const company = useGetCompany(user?.companyId);

	const [searchParams] = useSearchParams();

	let searchFields: any[] = [];
	let urlFolder: string = '';
	let useDateRange: boolean = false;

	switch (user?.companyTypeId) {
		case CompanyTypes.CFI:
			urlFolder = 'cfi';
			searchFields = [
				{ text: "RO #", field: "RONumber" },
				{ text: "MO #", field: "MONumber" },
				{ text: "Customer Name", field: "CustomerName" },
				{ text: "Customer Phone", field: "CustomerPhone" },
				{ text: "Project #", field: "ProjectNumber" },
				{ text: "Distributor Invoice #", field: "DistributorInvoiceNumber" },
				{ text: "Sage PO #", field: "PONumber" },
				{ text: "SKU", field: "SKU" },
				{ text: "CFI Payment #", field: "CFIInvoiceNumber" },
				{ text: "Estimate #", field: "EstimateNumber" }
			];

			searchFields.push({ text: "Work Order #", field: "WONumber" });

			if (company.data?.SFI) {
				searchFields.push({ text: "Lead #", field: "LeadNumber" });
			}
			break;
		case CompanyTypes.DEALER:
			urlFolder = 'dealer';
			useDateRange = true;
			searchFields = [
				{ text: "Order #", field: "OrderNumber" },
				{ text: "Customer Name", field: "CustomerName" },
				{ text: "Customer Address", field: "CustomerAddress" },
				{ text: "Customer Phone", field: "CustomerPhone" },
				{ text: "Project #", field: "ProjectNumber" },
				{ text: "Estimate #", field: "EstimateNumber" }
			];
			break;
		case CompanyTypes.DISTRIBUTOR:
			urlFolder = 'distributor';
			searchFields = [
				{ text: "Purchase Order #", field: "PONumber" },
				{ text: "Retail Order #", field: "RONumber" },
				{ text: "CFI Material PO Number", field: "COMPONumber" },
				{ text: "Sage Material Order #", field: "MONumber" },
				{ text: "Invoice Number", field: "InvoiceNumber" },
				{ text: "CFI Name", field: "CFIName" }
			];
			break;
		case CompanyTypes.MFR:
			urlFolder = 'manufacturer';
			searchFields = [
				{ text: "Purchase Order #", field: "PONumber" },
				{ text: "Retail Order #", field: "RONumber" },
				{ text: "CFI Material PO Number", field: "COMPONumber" },
				{ text: "Sage Material Order #", field: "MONumber" },
				{ text: "Invoice Number", field: "InvoiceNumber" },
				{ text: "CFI Name", field: "CFIName" }
			];
			break;
		case CompanyTypes.SERVICE_PROVIDER:
			urlFolder = 'service-provider';
			searchFields = [
				{ text: "Purchase Order #", field: "PONumber" },
				{ text: "Retail Order #", field: "RONumber" },
				{ text: "Invoice Number", field: "InvoiceNumber" },
				{ text: "CFI Name", field: "CFIName" }
			];
			break;
	}

	const [state, setState] = useState({
		searchField: searchParams.get("searchField") ?? (searchFields.length > 0 ? searchFields[0].field : undefined),
		searchText: searchParams.get("searchText") ?? "",
		dateRange: [
			searchParams.get("dateFrom") ?? formatIsoDate(dayjs().subtract(3, 'month').toDate()),
			searchParams.get("dateTo") ?? formatIsoDate(new Date())
		]
	});

	const submit = (e: any) => {

		e.preventDefault();

		navigate(
			`/${urlFolder}/orders/search?searchField=${state.searchField}&searchText=${state.searchText}` +
			(useDateRange ? `&dateFrom=${state.dateRange[0]}&dateTo=${state.dateRange[1]}` : ''));
	};

	return (
		<form className='search-panel' onSubmit={submit}>
			<div className='search-field'>
				<SelectBox
					valueExpr='field'
					displayExpr='text'
					dataSource={searchFields}
					value={state.searchField}
					onValueChanged={(e) => setState({ ...state, searchField: e.value })}
					inputAttr={{ "aria-label": "Search By" }}
					dropDownOptions={{ wrapperAttr: { class: 'header-search-dropdown' } }} 
				></SelectBox>
			</div>
			<div className='search-value'>
				<TextBox
					value={state.searchText}
					onValueChanged={(e) => setState({ ...state, searchText: e.value })}
					showClearButton={useDateRange}
					inputAttr={{ "aria-label": "Search Value" }}
				>
					{
						!useDateRange &&
						<Validator validationGroup="searchValidationGroup">
							<RequiredRule message='Search value is required' />
						</Validator>
					}
				</TextBox>
			</div>
			{
				useDateRange &&
				<div className='search-date-range'>
					<DateRangeBox
						value={state.dateRange}
						onValueChanged={(e) => {
							setState({
								...state,
								dateRange: e.value
							});
						}}
					/>
				</div>
			}
			<div className='search-button'>
				<Button
					hint="Search"
					useSubmitBehavior={true}
					validationGroup="searchValidationGroup"
				>
					<i className='dx-icon dx-icon-search'></i>
				</Button>
			</div>
		</form>
	);
}
